import { Component, OnInit, Input } from '@angular/core';
import { RestApiService } from '../../core/services/rest-api.service';
import { TranslateService } from '@ngx-translate/core';
import { ToastService } from 'src/app/core/services/toast-service';
import {
  FormBuilder,
  FormGroup,
  FormArray,
  FormControl,
  Validators,
  FormControlOptions,
} from '@angular/forms';
import { PipedriveService, PipedriveNotConfiguredError } from 'src/app/core/services/pipedrive.service';

@Component({
  selector: 'app-pipedrive',
  templateUrl: './pipedrive.component.html',
  styleUrl: './pipedrive.component.scss',
})
export class PipedriveComponent implements OnInit {
  breadCrumbItems!: Array<{}>;
  allFlows: Array<any> = [];
  isUpdating: boolean = false;
  initialPipedriveSettings: any = {};
  @Input() pipedriveForm: FormGroup;

  constructor(
    public restApiService: RestApiService,
    public pipedriveService: PipedriveService,
    public translate: TranslateService,
    public toastService: ToastService,
    private fb: FormBuilder
  ) {

    this.pipedriveForm = this.fb.group({
      baseUrl: [{ value: '', disabled: true }, Validators.required],
      apiPath: [{ value: '', disabled: true }, Validators.required],
      apiToken: [{ value: '', disabled: true }, Validators.required],
      licenseFlowId: '',
      licenseFlowName: '',
      hostFlowId: '',
      hostFlowName: '',
    });
    
  }

  ngOnInit(): void {
     
      this.restApiService.get('/pipedrive').subscribe({
        next: (response) => {
          
          this.initialPipedriveSettings = JSON.parse(response.data);

          this.loadFormFromInitialState();
        },
        error: (error) => {
          // 400 = Pipedrive ainda não configurado (primeiro acesso).
          // Não exibe toast de erro: esta é a tela onde a configuração é criada.
          if (error?.status === 400 || error?.statusCode === 400) {
            return;
          }

          this.toastService.show(error.message, {
            classname: 'bg-danger text-light',
          });
        },
      });
      
    

    this.breadCrumbItems = [
      { label: this.translate.instant('HOME') },
      { label: this.translate.instant('SETTINGS') },
      { label: this.translate.instant('PIPEDRIVE.NAME'), active: true },
    ];  
    
  }

  // convenience getter for easy access to form fields
  get f() {
    return this.pipedriveForm.controls;
  }

  get hasFlows(): boolean {
    return this.allFlows.length > 0;
  }

  onEdit(e: object): void {
    if (this.allFlows.length > 0) {
      this.enableForm();
      return;
    }

    this.pipedriveService.pipelines().subscribe({
      next: (res) => {
        this.allFlows = res.data;

        this.enableForm();
      },
      error: (err) => {
        const message = err instanceof PipedriveNotConfiguredError
          ? this.translate.instant('PIPEDRIVE.NOT_CONFIGURED')
          : this.translate.instant('PIPEDRIVE.UNABLE_TO_LOAD_FLOWS');

        this.toastService.show(message, {
          classname: 'bg-danger text-light',
        });

        this.disableForm();
        this.allFlows = [];
      },
    });
  }

  onCancelEdit(e: object): void {
    this.loadFormFromInitialState();
    this.disableForm();
  }

  loadFormFromInitialState(): void {
    const settings = this.initialPipedriveSettings ?? {};

    const baseUrl = settings['baseUrl'] ?? this.deriveBaseUrl(settings['urlApi']);
    const apiPath = settings['apiPath'] ?? this.deriveApiPath(settings['urlApi']);

    this.pipedriveForm.get('baseUrl')?.setValue(baseUrl);
    this.pipedriveForm.get('apiPath')?.setValue(apiPath);
    this.pipedriveForm.get('apiToken')?.setValue(settings['apiToken']);
    this.pipedriveForm
      .get('licenseFlowId')
      ?.setValue(settings['licenseFlow']?.['id']);
    this.pipedriveForm
      .get('licenseFlowName')
      ?.setValue(settings['licenseFlow']?.['name']);
    this.pipedriveForm
      .get('hostFlowId')
      ?.setValue(settings['hostFlow']?.['id']);
    this.pipedriveForm
      .get('hostFlowName')
      ?.setValue(settings['hostFlow']?.['name']);
  }

  // Backward compatibility: legacy config stored a single `urlApi`.
  private deriveBaseUrl(urlApi: string): string {
    if (!urlApi) {
      return '';
    }

    try {
      return new URL(urlApi).origin;
    } catch {
      return '';
    }
  }

  private deriveApiPath(urlApi: string): string {
    if (!urlApi) {
      return '';
    }

    try {
      return new URL(urlApi).pathname;
    } catch {
      return '';
    }
  }

  Submit(e: object): void {

    let selectedFlows = this.pipedriveForm.get('licenseFlowId')?.value !== '' &&
                        this.pipedriveForm.get('hostFlowId')?.value !== '';
                       
    // Check if the form is valid
    if (this.pipedriveForm.invalid || !selectedFlows) {
      this.toastService.show(this.translate.instant('PIPEDRIVE.FORM_INVALID'), {
        classname: 'bg-danger text-light',
      });
      return;
    }

    const baseUrl = (this.pipedriveForm.get('baseUrl')?.value ?? '').replace(/\/+$/, '');
    const apiPath = this.pipedriveForm.get('apiPath')?.value ?? '';

    let postData = {
      baseUrl: baseUrl,
      apiPath: apiPath,
      // Kept for backward compatibility with consumers still reading `urlApi`.
      urlApi: `${baseUrl}/${apiPath.replace(/^\/+/, '')}`.replace(/\/+$/, ''),
      apiToken: this.pipedriveForm.get('apiToken')?.value,
      licenseFlow: {
        id: this.pipedriveForm.get('licenseFlowId')?.value,
        name: this.allFlows.find(
          (flow) => flow.id == this.pipedriveForm.get('licenseFlowId')?.value
        )?.name,
      },
      hostFlow: {
        id: this.pipedriveForm.get('hostFlowId')?.value,
        name: this.allFlows.find(
          (flow) => flow.id == this.pipedriveForm.get('hostFlowId')?.value
        )?.name,
      },
    };

    this.restApiService.post('/pipedrive', postData).subscribe({
      next: (response) => {
        this.toastService.show(response.message, {
          classname: 'bg-success text-light',
        });

        this.initialPipedriveSettings = postData;

        // Atualiza o serviço singleton com a nova config (evita precisar recarregar a página).
        this.pipedriveService.loadConfig();

        this.disableForm();
        this.loadFormFromInitialState();
      },
      error: (error) => {
        this.toastService.show(error.message, {
          classname: 'bg-danger text-light',
        });
      },
    });
  }

  enableForm(): void {
    this.pipedriveForm.get('baseUrl')?.enable();
    this.pipedriveForm.get('apiPath')?.enable();
    this.pipedriveForm.get('apiToken')?.enable();
    this.isUpdating = true;
  }
  
  disableForm(): void {
    this.isUpdating = false;
    this.pipedriveForm.get('baseUrl')?.disable();
    this.pipedriveForm.get('apiPath')?.disable();
    this.pipedriveForm.get('apiToken')?.disable();
  }
}
